"use client"

/**
 * AppLayout — conditional shell wrapper + auth gate for scaffold-generated routes.
 *
 * Handles two concerns:
 * 1. Shell toggle: ?shell=true/false query param, with app-configurable default
 * 2. Auth check: shell always requires auth; private model routes require auth
 *
 * For public model routes (publicRoute=true), the page loads without auth.
 * The API handles per-instance authorisation separately.
 *
 * @example
 * // In a generated route layout:
 * <AppLayout publicRoute={false}>{children}</AppLayout>
 */

import React, { Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { useSimpleAuth } from '@sourcepilot/ui/shell/auth/use-simple-auth';
import { NoOrganisation, OrgRemovedNotice, useOrgRemoved } from '@sourcepilot/ui/shell/auth/org-removed';
import { SignInNoticeBanner, useSignInNotice } from '@sourcepilot/ui/shell/auth/sign-in-notice';
import { SessionChangeNotice } from '@sourcepilot/ui/shell/auth/session-change-notice';
import { OrganisationChangeStuckNotice } from '@sourcepilot/ui/shell/auth/organisation-change-stuck';
import { Spinner } from '@sourcepilot/ui/framework/ui/atoms/spinner';
import { Shell } from '@sourcepilot/ui/shell/Shell';
import { useAppHost } from '@sourcepilot/ui/framework/state/AppHostContext';

interface AppLayoutProps {
  children: React.ReactNode;
  /** Whether this route's model is publicly viewable (e.g. Industry, Material) */
  publicRoute?: boolean;
}

function AppLayoutInner({ children, publicRoute = false }: AppLayoutProps) {
  const searchParams = useSearchParams();
  const { user, isLoading, refetch } = useSimpleAuth();
  const { shellDefault } = useAppHost();
  // removed from the organisation the session acted as (VC-Authz): said
  // once; with none left, a page saying so rather than a wait
  const orgRemoved = useOrgRemoved(user as Parameters<typeof useOrgRemoved>[0]);
  // "you're now the owner" after a company administrator's sign-in (plan 81)
  const signInNotice = useSignInNotice(user);

  // Shell mode: query param overrides app default
  const shellParam = searchParams.get('shell');
  const showShell = shellParam !== null
    ? shellParam === 'true'
    : shellDefault;

  // Auth: shell always requires auth. Private routes require auth.
  const requiresAuth = showShell || !publicRoute;

  // Session-expiry watchdog. Auth is fetched ONCE at app init; a session
  // that dies MID-session leaves the stale user in the atom, so every
  // query 401s behind loading spinners and the redirect below never
  // re-evaluates (the "logged out but pages loop loading" bug).
  // Re-validate: instantly when the auth cookie disappears (checked
  // every 5s — catches logout in another tab / cookie expiry), and
  // every 60s regardless (covers server-side session invalidation
  // where the cookie lingers). A 401 nulls the user atom and the
  // redirect fires on the next render. refetch() is dedup'd
  // module-side, so overlapping timers cost one request.
  React.useEffect(() => {
    if (!requiresAuth) return;
    const hasAuthCookie = () =>
      document.cookie
        .split(';')
        .some((c) => c.trim().startsWith('auth_status='));
    const cookieWatch = setInterval(() => {
      if (!hasAuthCookie()) refetch();
    }, 5_000);
    const sessionWatch = setInterval(() => refetch(), 60_000);
    return () => {
      clearInterval(cookieWatch);
      clearInterval(sessionWatch);
    };
  }, [requiresAuth, refetch]);

  // Auth redirect — only after loading completes (not while checking)
  if (!isLoading && requiresAuth && !user) {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('returnUrl', window.location.pathname + window.location.search);
      window.location.href = '/login';
    }
    return null;
  }

  const content = (
    <>
      <OrganisationChangeStuckNotice />
      <OrgRemovedNotice removed={orgRemoved.removed} onDismiss={orgRemoved.dismiss} />
      <SignInNoticeBanner notice={signInNotice.notice} onDismiss={signInNotice.dismiss} />
      <SessionChangeNotice />
      {orgRemoved.noOrganisation ? <NoOrganisation removed={orgRemoved.removed} /> : children}
    </>
  );

  if (showShell) {
    return <Shell>{content}</Shell>;
  }

  return content;
}

export function AppLayout(props: AppLayoutProps) {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center h-screen">
        <Spinner />
      </div>
    }>
      <AppLayoutInner {...props} />
    </Suspense>
  );
}
